import { ROUTES } from "./routeConfig";

/**
 * Every role scope's own dashboard — the single source of truth for
 * "where does this user actually belong". Used by PublicRoute (redirect
 * an already-logged-in user away from /login), and by the "Back to my
 * panel" button every view-as preview shows (see PreviewBanner.tsx).
 */
export const DASHBOARD_BY_SCOPE: Record<string, string> = {
  SUPER_ADMIN: ROUTES.SUPER_ADMIN.DASHBOARD,
  ORG: ROUTES.ORG_ADMIN.DASHBOARD,
  CAMPUS: ROUTES.CAMPUS_ADMIN.DASHBOARD,
  INSTITUTION: ROUTES.INSTITUTION_ADMIN.DASHBOARD,
  STUDENT_PARENT: ROUTES.STUDENT_PARENT.DASHBOARD,
  TEACHER: ROUTES.TEACHER_PORTAL.DASHBOARD,
  STUDENT: ROUTES.STUDENT_PORTAL.DASHBOARD,
  PARENT: ROUTES.STUDENT_PORTAL.DASHBOARD,
  ACCOUNTANT: ROUTES.STAFF_PORTAL.DASHBOARD,
  LIBRARIAN: ROUTES.STAFF_PORTAL.DASHBOARD,
  DYNAMIC: ROUTES.STAFF_PORTAL.DASHBOARD,
};

/**
 * View-as ("go to their panel") permission table — who can preview whose
 * panel, read-only, using their OWN JWT (no impersonation/new token
 * issued). This only controls whether the frontend router lets the page
 * render; the backend independently re-checks tenant-hierarchy access on
 * every actual data request (canAccessOrganization/canAccessCampus/
 * canAccessInstitution in utils/tenantAccess.js) — this table can never
 * grant real data access the backend wouldn't already allow, it only
 * decides whether showing the preview UI at all makes sense.
 *
 * Query param name doubles as the flag that a request is "in preview
 * mode" at all — its mere presence on an allowed route is what triggers
 * the preview banner.
 */
export const VIEW_AS_RULES: Record<string, { param: string; targetRole: string }[]> = {
  SUPER_ADMIN: [
    { param: "viewOrganizationId", targetRole: "ORG" },
    { param: "viewCampusId", targetRole: "CAMPUS" },
    { param: "viewInstitutionId", targetRole: "INSTITUTION" },
  ],
  ORG: [
    { param: "viewCampusId", targetRole: "CAMPUS" },
    { param: "viewInstitutionId", targetRole: "INSTITUTION" },
  ],
  CAMPUS: [
    { param: "viewInstitutionId", targetRole: "INSTITUTION" },
  ],
};

/** True if `viewerScope` is allowed to preview a panel whose allowedRoles include one of VIEW_AS_RULES' targetRoles, given the current URL's search params. */
export function findViewAsMatch(viewerScope: string | undefined, allowedRoles: string[] | undefined, searchParams: URLSearchParams) {
  if (!viewerScope || !allowedRoles) return undefined;
  return VIEW_AS_RULES[viewerScope]?.find(
    (rule) => allowedRoles.includes(rule.targetRole) && searchParams.has(rule.param)
  );
}
